import { lazy, Suspense, useEffect, useRef } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  useLocation,
} from 'react-router-dom';

import type { Transaction } from '@elastic/apm-rum';

import { NotFound } from '@/shared/pages/NotFound/NotFound';
import { LaunchDarklyProvider } from '@/shared/contexts/launch-darkly';
import { getRum } from '@/shared/rum/apm';
import { trackPageView } from '@/analytics/gtm';
import { Forbidden } from '@/shared/pages/Forbidden/Forbidden';
import { URL_SEGMENTS } from '@/apps/autoops/modules/constants/paths';
import { useLegacyQueryParamRedirect } from '@/shared/hooks/useLegacyQueryParamRedirect';
import { FullPageLoader } from '@/shared/components/FullPageLoader/FullPageLoader';

const ServerlessRoutes = lazy(() => import('@/apps/serverless/routes'));
const CcmRoutes = lazy(() => import('@/apps/ccm/routes'));
const AutoOpsRoutes = lazy(() => import('@/apps/autoops/routes'));

function Shell() {
  const location = useLocation();
  useLegacyQueryParamRedirect();
  const transactionRef = useRef<Transaction | null>(null);

  useEffect(() => {
    const apm = getRum();
    if (!apm) return;

    if (transactionRef.current) transactionRef.current.end();

    const name = location.pathname || '/';
    const transaction = apm.startTransaction(name, 'route-change', {
      managed: true,
      canReuse: true,
    });

    transactionRef.current = transaction ?? null;
  }, [location.pathname, location.search]);

  useEffect(() => {
    trackPageView();
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.getElementById('app-main-scroll')?.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <Suspense fallback={<FullPageLoader />}>
      <LaunchDarklyProvider>
        <Outlet />
      </LaunchDarklyProvider>
    </Suspense>
  );
}

export default function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Shell />}>
          <Route
            path="/regions/:region/organizations/:organizationId/projects/:projectId/*"
            element={<ServerlessRoutes />}
          />
          <Route
            path="/organizations/:organizationId/connect-autoops/*"
            element={<CcmRoutes />}
          />
          <Route
            path={`/regions/:region/organizations/:organizationId/${URL_SEGMENTS.DEPLOYMENTS}/:deploymentId/*`}
            element={<AutoOpsRoutes />}
          />
          {/* :clusterId holds the internal deployment ID, not the external cluster ID */}
          <Route
            path={`/regions/:region/organizations/:organizationId/${URL_SEGMENTS.CLUSTERS}/:clusterId/*`}
            element={<AutoOpsRoutes />}
          />
          <Route path="forbidden" element={<Forbidden />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
